import $http from '../../shared/http';
import { DateTime } from 'luxon';
import { downloadFileFromResponse } from '../../shared/utils';

export const user_profiles = {
  namespaced: true,
  state: () => {
    return {
      userProfiles: [],
      userProfileRegisterData: {},
      currentEditUserProfile: {},
      currentUserProfile: {},
      userProfileEditData: {},
      isUserProfileEdited: false,
      membersForSearchSelect: null,
      loginMembersForSelect: null,
      isUserProfileCreated: false,
      newUserProfileID: null,
      profilePicture: null,
    };
  },
  getters: {
    getUserProfiles: (state) => state.userProfiles,
    getCurrentEditUserProfile: (state) => state.currentEditUserProfile,
    getCurrentUserProfile: (state) => state.currentUserProfile,
    isUserProfileEdited: (state) => state.isUserProfileEdited,
    getMembersForSearchSelect: (state) => state.membersForSearchSelect,
    getLoginMembersForSelect: (state) => state.loginMembersForSelect,
    getUserProfileRegisterData: (state) => state.userProfileRegisterData,
    isUserProfileCreated: (state) => state.isUserProfileCreated,
    getNewUserProfileID: (state) => state.newUserProfileID,
    getUserProfileForm: (state) => {
      return state.userProfileRegisterData;
    },
    getProfilePictureFormData: (state) => {
      const formData = new FormData();
      if (state.profilePicture) {
        formData.append('profile_picture', state.profilePicture);
        return formData;
      } else return null;
    },
    getServiceInvolvementCollection: (state, getters) => {
      const SCHEDULE_TYPE = 'schedule';
      const SERVICE_TYPE = 'service';

      const activitiesSchedules = getters.getCurrentUserProfile?.serviceActivitiesSchedules ?? [];

      const attendedServices = getters.getCurrentUserProfile?.attendedServices ?? [];

      if (!activitiesSchedules.length && !attendedServices.length) return [];
      else {
        let collector = [];

        if (activitiesSchedules && activitiesSchedules.length) {
          activitiesSchedules.forEach((schedule, index) => {
            const activityName = schedule?.serviceActivity?.alternative_name ?? null;
            const activityType = schedule?.service?.serviceType?.service_type_name ?? null;
            const activityDate = schedule?.service?.service_date ?? null;

            // Check if date key already exists inside collector
            let indexWhereActivityDateExistsInCollector;
            const dateExists = collector.some((collectorItem, collectorIndex) => {
              if (collectorItem.date && collectorItem.date === activityDate) {
                indexWhereActivityDateExistsInCollector = collectorIndex;
                return true;
              } else return false;
            });
            if (dateExists) {
              collector[indexWhereActivityDateExistsInCollector].involvements.push({
                name: `${activityName} of ${activityType}`,
                type: SCHEDULE_TYPE,
              });
            } else {
              collector[index] = {};
              // Do not format the date here. It will break the sort of the date later
              collector[index].date = activityDate;
              collector[index].involvements = [];
              collector[index].involvements.push({
                name: `${activityName} of ${activityType}`,
                type: SCHEDULE_TYPE,
              });
            }
          });
        }

        if (attendedServices && attendedServices.length) {
          attendedServices.forEach((service, index) => {
            const serviceTypeName = service?.serviceType?.service_type_name ?? null;
            const serviceDate = service?.service_date ?? null;

            if (collector[index]) {
              // Check if date key already exists inside collector
              let indexWhereActivityDateExistsInCollector;
              const dateExists = collector.some((collectorItem, collectorIndex) => {
                if (collectorItem.date && collectorItem.date === serviceDate) {
                  indexWhereActivityDateExistsInCollector = collectorIndex;
                  return true;
                } else return false;
              });
              if (dateExists) {
                collector[indexWhereActivityDateExistsInCollector].involvements.push({
                  name: `Attended ${serviceTypeName}`,
                  type: SERVICE_TYPE,
                });
              } else {
                collector[index].involvements.push({
                  name: `Attended ${serviceTypeName}`,
                  type: SERVICE_TYPE,
                });
              }
            } else {
              collector[index] = {};
              // Do not format the date here. It will break the sort of the date later
              collector[index].date = serviceDate;
              collector[index].involvements = [];
              collector[index].involvements.push({
                name: `Attended ${serviceTypeName}`,
                type: SERVICE_TYPE,
              });
            }
          });
        }

        collector = collector
          .filter((involvement) => involvement && involvement.date)
          .sort((a, b) => {
            if (a.date > b.date) return 1;
            if (a.date < b.date) return -1;
            return 0;
          })
          // Formatting of date should be done here
          .map((involvement) => {
            involvement.date = DateTime.fromISO(involvement.date).toFormat('DDDD');
            return involvement;
          })
          .reverse();

        //console.log(JSON.parse(JSON.stringify(collector)));
        return collector;
      }
    },
  },
  mutations: {
    setUserProfiles: (state, userPayload) => {
      state.userProfiles = userPayload;
    },
    setCurrentUserProfile: (state, userPayload) => {
      state.currentUserProfile = userPayload;
    },
    setUserProfileRegisterData: (state, payload) => (state.userProfileRegisterData = payload),
    setCurrentEditUserProfile: (state, payload) => {
      state.currentEditUserProfile = payload;
    },
    setUserProfileEditData: (state, payload) => {
      state.userProfileEditData = payload;
    },
    setUserProfileEdited: (state, status) => (state.isUserProfileEdited = status),
    initUserProfileRegisterData: (state) => {
      state.userProfileRegisterData = {};
    },
    setMembersForSearchSelect: (state, payload) => (state.membersForSearchSelect = payload),
    setLoginMembersForSelect: (state, payload) => (state.loginMembersForSelect = payload),
    resetMembersForSearchSelect: (state) => (state.membersForSearchSelect = null),
    setUserProfileCreated: (state, payload) => (state.isUserProfileCreated = payload),
    setNewUserProfileID: (state, payload) => (state.newUserProfileID = payload),
    storeProfilePicture: (state, file) => {
      if (file) {
        state.profilePicture = file;
      }
    },
    appendFormData(state, { key, content }) {
      state.profilePictureFormData.append(key, content);
    },
    clearRegisterResponseData: (state) => {
      state.newUserProfileID = null;
      state.isUserProfileCreated = null;
    },
  },
  actions: {
    async fetchChurchProfileAction({ commit }, { profileId, churchId }) {
      commit('setCurrentUserProfile', null);
      commit('setUserProfileEditData', null);
      return $http('v2')
        .get(`organisations/${churchId}/members/${profileId}`)
        .then((response) => {
          commit('setCurrentUserProfile', response.data.data);
          commit('setUserProfileEditData', response.data.data);
          return response;
        });
    },

    async loadMembersForSearchSelect({ commit }, { searchQuery, organisation_id, usage }) {
      commit('setMembersForSearchSelect', null);

      return $http('v2')
        .get(`organisations/${organisation_id}/members/select`, {
          params: {
            full_name: searchQuery,
            usage,
          },
        })
        .then((response) => {
          if (usage !== 'login_members_only') {
            commit('setMembersForSearchSelect', response.data.data);
          }
          return response;
        });
    },

    async LOAD_LOGIN_MEMBERS_FOR_SELECT({ dispatch, commit }, { organisation_id }) {
      return dispatch('loadMembersForSearchSelect', {
        organisation_id,
        usage: 'login_members_only',
      }).then((response) => {
        commit('setLoginMembersForSelect', response.data.data);
        return response;
      });
    },
    async CREATE_USER_PROFILE_ACTION({ commit, getters, rootGetters }, churchId) {
      // Step 1: Create the user profile with the registration form data first.
      // Profile picture is not uploaded at this state
      commit('setUserProfileCreated', false);
      commit('setNewUserProfileID', null);
      const isLoggedInForSelfRegistration =
        rootGetters['membership_self_registrations/isLoggedInForSelfRegistration'];
      const selfRegistrationToken =
        rootGetters['membership_self_registrations/getSelfRegistrationToken'];

      if (!churchId && !isLoggedInForSelfRegistration) {
        throw new Error('Church ID not provided');
      }

      return $http('v2')
        .post(
          isLoggedInForSelfRegistration
            ? `membership_self_registrations`
            : `/organisations/${churchId}/members`,
          getters['getUserProfileForm'],
          {
            headers: {
              'X-Is-Notifiable': 'On',
            },
            canBeImpersonated: true,
            params: {
              token: isLoggedInForSelfRegistration ? selfRegistrationToken : undefined,
            },
          }
        )
        .then(async (response) => {
          commit('setNewUserProfileID', response.data.data.id);
          commit('setUserProfileCreated', true);

          return response;
        })
        .catch((error) => {
          commit('setUserProfileCreated', false);
          return Promise.reject(error);
        });
    },
    async checkMembershipIDUniqueness(ctx, ID) {
      return $http()
        .get('/members/profile/isMemberShipUnique', {
          params: {
            membership_id: ID,
          },
        })
        .then((response) => {
          return response.data.data;
        })
        .catch((error) => {
          return Promise.reject(error.data);
        });
    },

    async saveProfilePictureAction({ getters }, { profileId, orgId }) {
      return $http('v2', { isMultipartData: true })
        .post(
          `organisations/${orgId}/members/${profileId}/profile_picture`,
          getters['getProfilePictureFormData'],
          {
            notifiable: true,
          }
        )
        .then((response) => {
          return response.data.data;
        });
    },
    async saveFormFieldAction(ctx, { ID: profileId, group, field, value, extra, churchId }) {
      return $http('v2')
        .patch(
          `organisations/${churchId}/members/${profileId}`,
          {
            group,
            field,
            value,
            extra,
          },
          { headers: { 'X-Is-Notifiable': 'On' } }
        )
        .then((response) => {
          return response.data.data;
        })
        .catch((error) => {
          return Promise.reject(error);
        });
    },

    async USER_PROFILES_MASS_ACTIONS(ctx, form) {
      return new Promise(async (resolve, reject) => {
        await $http()
          .patch(`/profiles/mass-actions`, form, { headers: { 'X-Is-Notifiable': 'On' } })
          .then((response) => {
            return resolve(response.data.data);
          })
          .catch((error) => {
            reject(error);
          });
      });
    },

    async DELETE_MEMBER_ACTION(ctx, { userId, churchId }) {
      return $http('v2')
        .delete(`organisations/${churchId}/users/${userId}`, { notifiable: true })
        .then((response) => {
          return response;
        });
    },

    async PRINT_MEMBERS_BIRTHDAYS(ctx, { churchId, query }) {
      return $http('v2')
        .get(`organisations/${churchId}/prints/membership_birthdays_report`, {
          responseType: 'blob',
          params: query,
          canBeImpersonated: true,
        })
        .then(async (response) => {
          return await downloadFileFromResponse(response, 'roster');
        })
        .catch((error) => {
          return Promise.reject(error);
        });
    },

    async CHURCH_PRINTING_ACTION(ctx, { churchId, query, type }) {
      return $http('v2')
        .get(`organisations/${churchId}/prints/${type}`, {
          responseType: 'blob',
          params: query,
          canBeImpersonated: true,
        })
        .then(async (response) => {
          return await downloadFileFromResponse(response, 'roster');
        })
        .catch((error) => {
          return Promise.reject(error);
        });
    },
    async UPDATE_PERSONAL_PROFILE(ctx, { form }) {
      return $http('v2', { isMultipartData: true }).put(`me`, form, { notifiable: true });
    },
    async UPDATE_PERSONAL_PROFILE_PICTURE(ctx, { form }) {
      return $http('v2', { isMultipartData: true }).put(`me/profile_picture`, form, {
        notifiable: true,
      });
    },
  },
};
